import type { Root, Paragraph } from 'mdast';
import { visit } from 'unist-util-visit';

/**
 * Extracts YouTube video ID from various URL formats or direct ID
 * Supports:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - Direct VIDEO_ID
 */
function extractYoutubeId(input: string): string | null {
  const trimmed = input.trim();

  // Direct video ID (11 characters)
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
    return trimmed;
  }

  // youtube.com/watch?v=VIDEO_ID
  const watchMatch = trimmed.match(/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
  if (watchMatch) {
    return watchMatch[1];
  }

  // youtu.be/VIDEO_ID
  const shortMatch = trimmed.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (shortMatch) {
    return shortMatch[1];
  }

  return null;
}

/**
 * Remark plugin to transform YouTube video references into embeds
 * Syntax: @youtube[VIDEO_ID or URL]
 */
function plugin() {
  return (tree: Root) => {
    visit(tree, 'paragraph', (node: Paragraph, index, parent) => {
      // Check if paragraph contains a single text node starting with @youtube
      if (node.children?.length === 1 && node.children[0].type === 'text') {
        const text = node.children[0].value as string;
        const match = text.match(/^@youtube\[(.+?)\]$/);

        if (match) {
          const videoId = extractYoutubeId(match[1]);

          if (videoId) {
            // Replace the paragraph node with a custom node
            if (!parent || index === undefined) return;

            // Modify the paragraph node to include custom data
            node.data = node.data || {};
            node.data.hName = 'youtube-embed';
            node.data.hProperties = {
              'data-youtube-id': videoId,
            };
          }
        }
      }
    });
  };
}

/**
 * Custom components for react-markdown
 */
const components = {
  'youtube-embed': ({ ...props }: ComponentsProps) => {
    const videoId = props['data-youtube-id'];

    if (videoId) {
      return (
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: '100%', marginBottom: '1rem' }}>
          <iframe
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }

    return null;
  },
};

type ComponentsProps = {
  'data-youtube-id'?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export const RemarkYoutubePlugin = {
  plugin,
  components,
};
